import { BUILD_TS } from '../build-info';
import { getRecording, onRecordingChange } from './recording-state';

/**
 * Detect when the deployed bundle is newer than the one this tab is
 * running and apply the update — either silently (idle user) or
 * deferred until the user stops recording (active user).
 *
 * Two detection paths:
 *  1. **Polling** — fetch `/version.json` every {@link POLL_MS}, compare
 *     `buildTs` to the constant baked into this bundle. Wins for the
 *     routine "we just shipped a fix" case.
 *  2. **Server push** — subscribe to `/v1/clients/control` (public SSE)
 *     and react to `{type:"reload"}` messages. Wins for the urgent
 *     "everyone reload NOW" case, triggered from the admin page.
 *
 * Apply logic is shared:
 *  - If `getRecording() === false`, reload immediately.
 *  - Else mark the update as pending and fire `onPending(now)` so the
 *    caller can show a banner. When recording stops, automatically
 *    reload via the recording-state listener.
 *
 * The poll endpoint and the SSE endpoint live behind the SAME public
 * ingress as the existing `/v1/events` ingest path, so this works for
 * every joltstream tab (PWA, APK, browser) without any extra auth.
 */

const POLL_MS = 60_000;
const VERSION_URL = '/version.json';
const STREAM_URL = '/v1/clients/control';

export interface VersionSyncEvents {
  /** Called when an update is detected but the user is recording.
   *  The banner UI should appear; reload will happen on stop().     */
  onPending?: () => void;
  /** Called when an update has been applied (just before reload).
   *  Useful for logging or analytics.                                */
  onApplying?: (reason: 'newer-version' | 'admin-broadcast') => void;
}

export interface VersionSyncHandle {
  /** Stop polling, close SSE. Mostly for tests. */
  stop(): void;
}

export function startVersionSync(events: VersionSyncEvents = {}): VersionSyncHandle {
  let stopped = false;
  let pending = false;
  let pendingReason: 'newer-version' | 'admin-broadcast' | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let eventSource: EventSource | null = null;

  const apply = (reason: 'newer-version' | 'admin-broadcast') => {
    if (stopped) return;
    if (getRecording()) {
      if (!pending) {
        pending = true;
        pendingReason = reason;
        events.onPending?.();
      }
      return;
    }
    events.onApplying?.(reason);
    // Use replace() rather than reload() — reload() keeps the back
    // entry, which can cause a "back" button to jump to the pre-update
    // bundle, surprising the user. replace() with same URL forces a
    // full reload while preserving navigation history sensibly.
    window.location.replace(window.location.href);
  };

  const unsubRecording = onRecordingChange((isRec) => {
    if (!isRec && pending && pendingReason) {
      apply(pendingReason);
    }
  });

  // 1. Polling loop
  const poll = async () => {
    if (stopped) return;
    try {
      const r = await fetch(VERSION_URL, { cache: 'no-store' });
      if (!r.ok) return;
      const body = (await r.json()) as { buildTs?: string };
      if (
        typeof body.buildTs === 'string' &&
        body.buildTs > BUILD_TS // ISO timestamps compare correctly as strings
      ) {
        apply('newer-version');
      }
    } catch {
      // Network error, JSON parse error — silently retry on the next tick.
    }
  };
  // Run once immediately at start so a tab that's been open through a
  // deploy picks up the update on the next interaction. Then on the timer.
  void poll();
  pollTimer = setInterval(() => void poll(), POLL_MS);

  // 2. Server-push SSE
  try {
    eventSource = new EventSource(STREAM_URL);
    eventSource.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data) as { type?: string };
        if (msg.type === 'reload') {
          apply('admin-broadcast');
        }
      } catch {
        // ignore malformed
      }
    };
    eventSource.onerror = () => {
      // EventSource retries automatically. Nothing for us to do.
    };
  } catch {
    // EventSource not available — polling will still catch updates.
  }

  return {
    stop() {
      stopped = true;
      if (pollTimer) clearInterval(pollTimer);
      if (eventSource) eventSource.close();
      unsubRecording();
    },
  };
}
